import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { useAutoUpdate } from "@/lib/useAutoUpdate";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Activate from "@/pages/Activate";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicWelcome from "@/pages/PublicWelcome";
import DashboardLayout from "@/components/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Houses from "@/pages/Houses";
import HouseDetail from "@/pages/HouseDetail";
import CalendarPage from "@/pages/CalendarPage";
import Reservations from "@/pages/Reservations";
import Tasks from "@/pages/Tasks";
import Templates from "@/pages/Templates";
import Users from "@/pages/Users";
import KeyManagement from "@/pages/KeyManagement";
import Chat from "@/pages/Chat";
import Billing from "@/pages/Billing";
import Linen from "@/pages/Linen";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== true && typeof user === "object") return <Navigate to="/app" replace />;
  return children;
}

function RoleGuard({ block, to, children }) {
  const { user } = useAuth();
  if (user && typeof user === "object" && block.includes(user.role)) return <Navigate to={to} replace />;
  return children;
}

function App() {
  useAutoUpdate();
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/welcome/:token" element={<PublicWelcome />} />
            <Route path="/portal/guest/:token" element={<PublicWelcome />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/activate" element={<Activate />} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="houses" element={<Houses />} />
              <Route path="houses/:id" element={<HouseDetail />} />
              <Route path="reservations" element={<RoleGuard block={["cleaner", "subcontractor", "host"]} to="/app/calendar"><Reservations /></RoleGuard>} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="chat" element={<Chat />} />
              <Route path="tasks" element={<RoleGuard block={["host"]} to="/app/calendar"><Tasks /></RoleGuard>} />
              <Route path="billing" element={<RoleGuard block={["host", "cleaner", "subcontractor", "guest"]} to="/app"><Billing /></RoleGuard>} />
              <Route path="linen" element={<RoleGuard block={["cleaner", "subcontractor", "client", "guest"]} to="/app"><Linen /></RoleGuard>} />
              <Route path="keys" element={<KeyManagement />} />
              <Route path="templates" element={<RoleGuard block={["host", "cleaner", "subcontractor", "client", "guest"]} to="/app"><Templates /></RoleGuard>} />
              <Route path="users" element={<RoleGuard block={["host", "cleaner", "subcontractor", "client", "guest"]} to="/app"><Users /></RoleGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
